/** Practice-Readiness Trials - 3 full ADC-style timed simulations. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Clock, Lock, CheckCircle2, Play, RotateCcw, FileText, Award } from "lucide-react";
import TrialPill from "@/components/TrialPill";

const statusBadge = (t) => {
  if (t.locked) return { label: "Pro", cls: "bg-muted text-muted-foreground" };
  if (t.status === "completed") return { label: `Scored ${t.score}%`, cls: "bg-success/15 text-success" };
  if (t.status === "in_progress") return { label: "In progress", cls: "bg-warning/15 text-warning" };
  return { label: "Ready", cls: "bg-primary/15 text-primary" };
};

const TrialsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get("/trials").then(setData).catch(() => toast.error("Could not load trials"));
  useEffect(() => { load(); }, []);

  const retake = async (n) => {
    if (!window.confirm("Retake this trial? Your previous attempt will be archived and a fresh 140-question paper generated.")) return;
    try {
      await api.post(`/trials/${n}/retake`);
      navigate(`/trials/${n}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not retake");
    }
  };

  if (!data) return <div className="py-20 text-center text-muted-foreground">Loading trials…</div>;

  return (
    <div data-testid="trials-page" className="space-y-8">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-bold">Practice-Readiness Trials</h1>
          <TrialPill />
        </div>
        <p className="text-muted-foreground mt-1 max-w-2xl">
          Three full ADC-style simulations - <strong>140 questions, 4 hours</strong>, weighted exactly like the
          real exam blueprint. Sit them at the start, middle and end of your prep so exam day feels familiar.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {data.trials.map((t) => {
          const badge = statusBadge(t);
          return (
            <div
              key={t.trial_number}
              data-testid={`trial-card-${t.trial_number}`}
              className={`rounded-2xl border border-border bg-card p-6 flex flex-col gap-4 ${t.locked ? "opacity-80" : ""}`}
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center font-bold text-lg">
                  {t.trial_number}
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{t.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t.tagline}</p>
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {t.num_questions} Qs</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {t.duration_minutes / 60}h timed</span>
                </div>
              </div>

              {t.locked ? (
                <Button variant="outline" className="gap-2" data-testid={`trial-unlock-${t.trial_number}`} onClick={() => navigate("/billing")}>
                  <Lock className="w-4 h-4" /> Unlock with Pro
                </Button>
              ) : t.status === "completed" ? (
                <div className="flex gap-2">
                  <Button className="flex-1 gap-2" data-testid={`trial-review-${t.trial_number}`} onClick={() => navigate(`/trials/${t.trial_number}?review=1`)}>
                    <CheckCircle2 className="w-4 h-4" /> Review
                  </Button>
                  <Button variant="outline" size="icon" title="Retake" data-testid={`trial-retake-${t.trial_number}`} onClick={() => retake(t.trial_number)}>
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button className="gap-2" data-testid={`trial-start-${t.trial_number}`} onClick={() => navigate(`/trials/${t.trial_number}`)}>
                  <Play className="w-4 h-4" /> {t.status === "in_progress" ? "Resume trial" : "Start trial"}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 flex items-start gap-4">
        <Award className="w-6 h-6 text-warning flex-shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">How the paper is built</p>
          <p>
            Every trial mirrors the ADC blueprint: Oral Surgery 15%, Perio &amp; Prostho 12%, Oral Medicine 12%,
            Endo/Operative/Ortho 10% each, Pharmacology 8%, Paeds 7%, Community 4%. Unanswered questions score
            zero - just like the real day. Your results feed your weak-area engine automatically.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrialsPage;
